import { z } from "zod";
import { fcfa, MOIS, useApiEnveloppe } from "../../api/hooks";
import { AnneauNotation, BarresDomaines, ChaineTraitement } from "../../composants/AnneauNotation";
import { EtatChargement, EtatErreur, ListeAvis } from "../../composants/Avis";
import type { EcranProps } from "../types";

const Schema = z.object({
  notation: z.object({
    evaluable: z.boolean(),
    noteGlobale: z.number().nullable(),
    cote: z.string(),
    motif: z.string().nullable(),
    affichage: z.string(),
    domaines: z.array(
      z.object({
        domaine: z.string(),
        libelle: z.string(),
        ponderation: z.number(),
        note: z.number(),
        nbConstats: z.number(),
      }),
    ),
  }),
  chaineDeTraitement: z.array(
    z.object({ libelle: z.string(), statut: z.string(), valeur: z.string() }),
  ),
  pointsPrioritaires: z.array(
    z.object({
      code: z.string(),
      libelle: z.string(),
      action: z.string().nullable(),
      gravite: z.string(),
      exposition: z.number(),
    }),
  ),
  gravites: z.object({ critique: z.number(), majeur: z.number(), moyen: z.number() }),
  gestion: z.object({
    resultat: z.number(),
    tresorerie: z.number(),
    caDuMois: z.number(),
    impotsDus: z.number(),
    resteAPayer: z.number(),
    penalites: z.number(),
    masseSalariale: z.number(),
    effectif: z.number(),
    exposition: z.number(),
  }),
  agence: z.object({
    interventions: z.number(),
    heures: z.number(),
    requetes: z.number(),
    requetesEnCours: z.number(),
    satisfaction: z.number().nullable(),
  }),
  dossier: z.object({
    raisonSociale: z.string().nullable(),
    regime: z.string().nullable(),
  }),
  indicateurs: z.object({
    pieces: z.number(),
    factures: z.number(),
    ecrituresJournal: z.number(),
    releveAnalyse: z.boolean(),
    salaries: z.number(),
    declarations: z.number(),
  }),
});

/** Une valeur mise en avant. La couleur n'est portée que si elle dit quelque chose. */
function Chiffre({
  valeur,
  legende,
  alerte,
}: {
  valeur: string;
  legende: string;
  alerte?: boolean;
}) {
  return (
    <div className="chiffre">
      <span className={`chiffre__valeur${alerte ? " chiffre__valeur--alerte" : ""}`}>{valeur}</span>
      <span className="chiffre__legende">{legende}</span>
    </div>
  );
}

export function TableauDeBord({ dossierId, annee, mois }: EcranProps) {
  const q = useApiEnveloppe(
    ["tableau-de-bord", dossierId, annee, mois],
    `/dossiers/${dossierId}/tableau-de-bord/${annee}/${mois}`,
    Schema,
  );

  if (q.isLoading) return <EtatChargement quoi="du tableau de bord" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <p className="a-faire">Aucune donnée sur la période.</p>;

  const { notation, chaineDeTraitement, pointsPrioritaires, gravites, gestion, agence, dossier } =
    q.data.valeur;

  return (
    <div className="tableau-de-bord">
      <AnneauNotation
        evaluable={notation.evaluable}
        note={notation.noteGlobale}
        cote={notation.cote}
        motif={notation.motif}
        titre={`Note de conformité — ${MOIS[mois - 1]} ${annee}`}
        sousTitre={
          dossier.raisonSociale
            ? `${dossier.raisonSociale}${dossier.regime ? ` · ${dossier.regime}` : ""}`
            : undefined
        }
        gravites={gravites}
      />

      <section>
        <h2>Chaîne de traitement du mois</h2>
        <ChaineTraitement etapes={chaineDeTraitement} />
      </section>

      <div className="deux-colonnes">
        <section>
          <h2>Conformité par domaine</h2>
          <BarresDomaines domaines={notation.domaines} />
        </section>

        <section>
          <h2>Indicateurs clés</h2>
          <div className="chiffres">
            <Chiffre valeur={fcfa(gestion.resultat)} legende="Résultat" alerte={gestion.resultat < 0} />
            <Chiffre
              valeur={fcfa(gestion.tresorerie)}
              legende="Trésorerie"
              alerte={gestion.tresorerie < 0}
            />
            <Chiffre valeur={fcfa(gestion.caDuMois)} legende="CA du mois" />
            <Chiffre valeur={fcfa(gestion.impotsDus)} legende="Impôts dus" />
            <Chiffre
              valeur={fcfa(gestion.resteAPayer)}
              legende="Reste à payer"
              alerte={gestion.resteAPayer > 0}
            />
            <Chiffre valeur={String(gestion.effectif)} legende="Effectif" />
            {gestion.penalites > 0 && (
              <Chiffre valeur={fcfa(gestion.penalites)} legende="Pénalités" alerte />
            )}
            {gestion.exposition > 0 && (
              <Chiffre valeur={fcfa(gestion.exposition)} legende="Exposition" alerte />
            )}
          </div>
        </section>
      </div>

      <div className="deux-colonnes">
        <section>
          <h2>Points à traiter en priorité</h2>
          {pointsPrioritaires.length === 0 ? (
            <p className="a-faire">Aucune anomalie constatée.</p>
          ) : (
            <ol className="prioritaires">
              {pointsPrioritaires.map((p) => (
                <li key={p.code} className={`prioritaire prioritaire--${p.gravite.toLowerCase()}`}>
                  <header>
                    <span className="prioritaire__code">{p.code}</span>
                    <span className="prioritaire__gravite">{p.gravite}</span>
                    {p.exposition > 0 && (
                      <span className="prioritaire__expo">{fcfa(p.exposition)}</span>
                    )}
                  </header>
                  <p className="prioritaire__libelle">{p.libelle}</p>
                  {p.action && <p className="prioritaire__action">{p.action}</p>}
                </li>
              ))}
            </ol>
          )}
        </section>

        <section>
          <h2>Activité de l'agence</h2>
          <div className="chiffres">
            <Chiffre valeur={String(agence.interventions)} legende="Interventions" />
            <Chiffre valeur={agence.heures.toFixed(1)} legende="Heures" />
            <Chiffre valeur={String(agence.requetes)} legende="Requêtes" />
            <Chiffre
              // Une moyenne sur aucune note n'est pas zéro : elle n'existe pas.
              valeur={agence.satisfaction === null ? "—" : `${agence.satisfaction} / 5`}
              legende="Satisfaction"
            />
          </div>
          {agence.requetesEnCours > 0 && (
            <p className="note">
              {agence.requetesEnCours} requête(s) encore en cours de traitement.
            </p>
          )}
        </section>
      </div>

      <ListeAvis avis={q.data.avis} titre="Observations des moteurs" />
    </div>
  );
}
